/**
 * Shared rating vocabularies for the item-rating feature.
 *
 * Mirrors the backend taxonomies in `backend/models/rating_model.py`. The label
 * values here are i18next keys resolved at render time (EN + DE).
 *
 * @module ratingConstants
 */

export type RatingTargetType = 'question' | 'ai_feedback';
export type RatingModule = 'reading' | 'listening' | 'writing' | 'speaking';
export type RatingValue = 'like' | 'dislike';

export interface ReasonOption {
  /** Stable reason code persisted server-side. */
  readonly code: string;
  /** i18next key for the human-readable label. */
  readonly labelKey: string;
}

/** Question dislike reasons (target_type = 'question'). */
export const QUESTION_DISLIKE_REASONS: readonly ReasonOption[] = [
  { code: 'ANSWER_KEY_WRONG', labelKey: 'rating.reasons.question.ANSWER_KEY_WRONG' },
  { code: 'UNCLEAR', labelKey: 'rating.reasons.question.UNCLEAR' },
  { code: 'LANGUAGE_ERROR', labelKey: 'rating.reasons.question.LANGUAGE_ERROR' },
  { code: 'BROKEN_MEDIA', labelKey: 'rating.reasons.question.BROKEN_MEDIA' },
  { code: 'WRONG_LEVEL', labelKey: 'rating.reasons.question.WRONG_LEVEL' },
  { code: 'OTHER', labelKey: 'rating.reasons.question.OTHER' },
] as const;

/** AI-feedback dislike reasons for writing/speaking (all six). */
export const AI_FEEDBACK_DISLIKE_REASONS_WRITING_SPEAKING: readonly ReasonOption[] = [
  { code: 'GRADE_UNFAIR', labelKey: 'rating.reasons.aiFeedback.GRADE_UNFAIR' },
  { code: 'NOT_UNDERSTOOD', labelKey: 'rating.reasons.aiFeedback.NOT_UNDERSTOOD' },
  { code: 'UNSURE_PASS', labelKey: 'rating.reasons.aiFeedback.UNSURE_PASS' },
  { code: 'TOO_GENERIC', labelKey: 'rating.reasons.aiFeedback.TOO_GENERIC' },
  { code: 'FEEDBACK_WRONG', labelKey: 'rating.reasons.aiFeedback.FEEDBACK_WRONG' },
  { code: 'OTHER', labelKey: 'rating.reasons.aiFeedback.OTHER' },
] as const;

/** AI-feedback dislike reasons for reading/listening (no grade dispute). */
export const AI_FEEDBACK_DISLIKE_REASONS_READING_LISTENING: readonly ReasonOption[] = [
  { code: 'NOT_UNDERSTOOD', labelKey: 'rating.reasons.aiFeedback.NOT_UNDERSTOOD' },
  { code: 'TOO_GENERIC', labelKey: 'rating.reasons.aiFeedback.TOO_GENERIC' },
  { code: 'FEEDBACK_WRONG', labelKey: 'rating.reasons.aiFeedback.FEEDBACK_WRONG' },
  { code: 'OTHER', labelKey: 'rating.reasons.aiFeedback.OTHER' },
] as const;

/** Reasons that make an AI-feedback dislike eligible for the human review offer. */
export const OFFER_TRIGGER_REASONS: ReadonlySet<string> = new Set([
  'GRADE_UNFAIR',
  'NOT_UNDERSTOOD',
  'UNSURE_PASS',
]);

const AUTOSCORED_MODULES: ReadonlySet<RatingModule> = new Set(['reading', 'listening']);

/** Reason options for a given target type + module. */
export function reasonsFor(
  targetType: RatingTargetType,
  module: RatingModule,
): readonly ReasonOption[] {
  if (targetType === 'question') {
    return QUESTION_DISLIKE_REASONS;
  }
  return AUTOSCORED_MODULES.has(module)
    ? AI_FEEDBACK_DISLIKE_REASONS_READING_LISTENING
    : AI_FEEDBACK_DISLIKE_REASONS_WRITING_SPEAKING;
}

export const FREE_TEXT_MAX = 500;
export const ATTACHED_QUESTION_MAX = 500;
export const NOTES_MAX = 1000;
